import React, { useState } from 'react';
import { useWorkspace } from '../../../context/WorkspaceContext';
import { Card } from '../../../design-system/Card';
import { Badge } from '../../../design-system/Badge';
import {
  Activity,
  Zap,
  Clock,
  AlertTriangle,
  TrendingUp,
  ShieldCheck
} from 'lucide-react';

export const ReportsView = () => {
  const { allTasks = [] } = useWorkspace();
  const [timeRange, setTimeRange] = useState('sprint'); // 'sprint', 'month', 'all'

  // Calculate Metrics from Task Data
  const totalTasks = allTasks.length;
  const completedTasks = allTasks.filter((t) => t.status === 'done').length;
  const inProgressTasks = allTasks.filter((t) => t.status === 'in_progress').length;
  const todoTasks = allTasks.filter((t) => t.status === 'todo' || t.status === 'backlog').length;
  const blockedTasks = allTasks.filter((t) => (t.blockedBy || []).length > 0).length;

  const projectHealthScore = Math.min(
    100,
    Math.max(60, Math.round(100 - blockedTasks * 8 - (totalTasks - completedTasks > 10 ? 5 : 0)))
  );

  // Discipline Workload breakdown
  const disciplineBreakdown = [
    { discipline: 'Core Engineering', count: 12, percent: 45, color: 'var(--accent-teal)' },
    { discipline: 'UI/UX & Design', count: 6, percent: 25, color: 'var(--color-purple)' },
    { discipline: 'Architecture & Docs', count: 4, percent: 18, color: 'var(--color-info)' },
    { discipline: 'QA & Hardware', count: 3, percent: 12, color: 'var(--color-warning)' }
  ];

  // Weekly Velocity Trajectory
  const weeklyVelocity = [
    { week: 'Week 1', points: 14, ideal: 12 },
    { week: 'Week 2', points: 18, ideal: 15 },
    { week: 'Week 3', points: 22, ideal: 20 },
    { week: 'Week 4 (Current)', points: 26, ideal: 24 }
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }} className="animate-fade-in">
      {/* Top Header & Range Switcher */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <Badge variant="teal" size="sm">Health & Diagnostics</Badge>
            <h2 style={{ fontSize: '20px', fontWeight: '800' }}>Project Health & Velocity Reports</h2>
          </div>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
            Systemic health metrics, cycle times, and discipline workload distribution
          </p>
        </div>

        <div style={{ display: 'flex', backgroundColor: 'var(--surface-recessed)', borderRadius: 'var(--radius-sm)', padding: '2px' }}>
          {[
            { id: 'sprint', label: 'Active Sprint' },
            { id: 'month', label: 'Last 30 Days' },
            { id: 'all', label: 'All-Time' }
          ].map((r) => (
            <button
              key={r.id}
              onClick={() => setTimeRange(r.id)}
              style={{
                padding: '6px 12px',
                borderRadius: 'var(--radius-sm)',
                backgroundColor: timeRange === r.id ? 'var(--surface-card)' : 'transparent',
                boxShadow: timeRange === r.id ? 'var(--shadow-raised-sm)' : 'none',
                border: 'none',
                color: timeRange === r.id ? 'var(--accent-teal)' : 'var(--text-muted)',
                fontSize: '12px',
                fontWeight: '700',
                cursor: 'pointer'
              }}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* Health Principle Banner (ADR-Principle) */}
      <div
        style={{
          padding: '14px 18px',
          backgroundColor: 'rgba(35, 190, 179, 0.08)',
          border: '1px solid rgba(35, 190, 179, 0.3)',
          borderRadius: 'var(--radius-md)',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}
      >
        <ShieldCheck size={22} color="var(--accent-teal)" />
        <div style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
          <strong>Core Principle:</strong> Forma measures <strong>Project Health & Delivery Velocity</strong> rather than individual employee surveillance or busyness.
        </div>
      </div>

      {/* Top 4 KPI Health Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '14px' }}>
        {/* Project Health Score */}
        <Card padding="20px">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)' }}>PROJECT HEALTH</span>
            <Activity size={18} color="var(--accent-teal)" />
          </div>
          <div style={{ fontSize: '28px', fontWeight: '900', color: 'var(--accent-teal)', lineHeight: '1' }}>
            {projectHealthScore}%
          </div>
          <div style={{ fontSize: '12px', color: 'var(--color-success)', marginTop: '6px', fontWeight: '600' }}>
            ● Healthy • On Schedule
          </div>
        </Card>

        {/* Velocity */}
        <Card padding="20px">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)' }}>DELIVERY VELOCITY</span>
            <Zap size={18} color="var(--color-warning)" />
          </div>
          <div style={{ fontSize: '28px', fontWeight: '900', color: 'var(--text-primary)', lineHeight: '1' }}>
            22 pts <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-muted)' }}>/ week</span>
          </div>
          <div style={{ fontSize: '12px', color: 'var(--accent-teal)', marginTop: '6px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '2px' }}>
            <TrendingUp size={13} /> +18% vs last sprint
          </div>
        </Card>

        {/* Cycle Time */}
        <Card padding="20px">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)' }}>AVG CYCLE TIME</span>
            <Clock size={18} color="var(--color-info)" />
          </div>
          <div style={{ fontSize: '28px', fontWeight: '900', color: 'var(--text-primary)', lineHeight: '1' }}>
            3.2 <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-muted)' }}>days</span>
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '6px' }}>
            From In-Progress to Done
          </div>
        </Card>

        {/* Bottlenecks & Blockers */}
        <Card padding="20px">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-muted)' }}>BLOCKED TASKS</span>
            <AlertTriangle size={18} color={blockedTasks > 0 ? 'var(--color-warning)' : 'var(--color-success)'} />
          </div>
          <div style={{ fontSize: '28px', fontWeight: '900', color: blockedTasks > 0 ? 'var(--color-warning)' : 'var(--color-success)', lineHeight: '1' }}>
            {blockedTasks}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '6px' }}>
            {blockedTasks === 0 ? 'Zero bottleneck dependencies' : '1 task waiting on FOR-101'}
          </div>
        </Card>
      </div>

      {/* Main Charts & Analytics Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '18px' }}>
        {/* Discipline Workload Distribution */}
        <Card padding="24px">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '800' }}>Discipline Workload Balance</h3>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Distribution across functional teams</span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {disciplineBreakdown.map((item) => (
              <div key={item.discipline} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                  <span style={{ fontWeight: '600' }}>{item.discipline}</span>
                  <span style={{ color: 'var(--text-muted)', fontWeight: '700' }}>{item.count} tasks ({item.percent}%)</span>
                </div>
                <div style={{ height: '7px', backgroundColor: 'var(--surface-recessed)', borderRadius: '999px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${item.percent}%`, backgroundColor: item.color, borderRadius: '999px' }} />
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Status Distribution & Velocity Trend */}
        <Card padding="24px">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '800' }}>Weekly Velocity Trajectory</h3>
            <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Story points completed per week</span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', alignItems: 'flex-end', height: '160px', paddingBottom: '10px' }}>
            {weeklyVelocity.map((w) => (
              <div key={w.week} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', height: '100%', justifyContent: 'flex-end' }}>
                <div style={{ fontSize: '11px', fontWeight: '800', color: 'var(--accent-teal)' }}>
                  {w.points} pts
                </div>
                <div
                  style={{
                    width: '100%',
                    maxWidth: '44px',
                    height: `${(w.points / 30) * 100}%`,
                    backgroundColor: 'var(--accent-teal)',
                    borderRadius: 'var(--radius-sm)',
                    boxShadow: 'var(--shadow-raised-sm)'
                  }}
                />
                <div style={{ fontSize: '10px', color: 'var(--text-muted)', textAlign: 'center' }}>
                  {w.week}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Task Status Flow Breakdown */}
      <Card padding="24px">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '800' }}>Cumulative Flow Status Breakdown</h3>
          <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{totalTasks} total tasks</span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px' }}>
          <div style={{ padding: '14px', backgroundColor: 'var(--surface-recessed)', borderRadius: 'var(--radius-md)', textAlign: 'center' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase' }}>To Do</div>
            <div style={{ fontSize: '22px', fontWeight: '900', marginTop: '4px' }}>{todoTasks}</div>
          </div>

          <div style={{ padding: '14px', backgroundColor: 'var(--surface-recessed)', borderRadius: 'var(--radius-md)', textAlign: 'center' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--accent-teal)', textTransform: 'uppercase' }}>In Progress</div>
            <div style={{ fontSize: '22px', fontWeight: '900', color: 'var(--accent-teal)', marginTop: '4px' }}>{inProgressTasks}</div>
          </div>

          <div style={{ padding: '14px', backgroundColor: 'var(--surface-recessed)', borderRadius: 'var(--radius-md)', textAlign: 'center' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--color-warning)', textTransform: 'uppercase' }}>Blocked</div>
            <div style={{ fontSize: '22px', fontWeight: '900', color: 'var(--color-warning)', marginTop: '4px' }}>{blockedTasks}</div>
          </div>

          <div style={{ padding: '14px', backgroundColor: 'var(--surface-recessed)', borderRadius: 'var(--radius-md)', textAlign: 'center' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--color-success)', textTransform: 'uppercase' }}>Done</div>
            <div style={{ fontSize: '22px', fontWeight: '900', color: 'var(--color-success)', marginTop: '4px' }}>{completedTasks}</div>
          </div>
        </div>
      </Card>
    </div>
  );
};
